import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, Search } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single source of truth for everything that can be hidden in the sidebar.
// Keep paths exactly in sync with DashboardLayout.jsx.
const MENU_ITEMS = [
  { group: 'Top', path: '/admin', label: 'Dashboard' },
  { group: 'Top', path: '/admin/group-overview', label: 'JSM Group Overview' },
  { group: 'Top', path: '/admin/tenders', label: 'Live Tenders' },
  { group: 'Top', path: '/admin/drivers', label: 'Owner Drivers' },
  { group: 'Top', path: '/admin/sites', label: 'Sites' },
  { group: 'Top', path: '/admin/credentials', label: 'Credentials' },
  { group: 'Top', path: '/admin/employees', label: 'Employees' },
  { group: 'Top', path: '/admin/clerks', label: 'Transport Clerks' },
  { group: 'Top', path: '/admin/tasks', label: 'Clerk Tasks & KPIs' },
  { group: 'Top', path: '/admin/performance', label: 'Performance & Incentives' },
  { group: 'Top', path: '/admin/internal-staff', label: 'Internal Staff & Payslips' },
  { group: 'Top', path: '/admin/staff-register', label: 'Staff Attendance Register' },
  { group: 'Top', path: '/admin/all-payslips', label: 'All Payslips (Group)' },
  { group: 'Top', path: '/admin/permissions', label: 'Admin Permissions' },
  { group: 'Top', path: '/admin/registrations', label: 'Registrations' },

  { group: 'Statements', path: '/admin/statements/generate', label: 'Generate Statements' },
  { group: 'Statements', path: '/admin/statements', label: 'Upload Statements' },
  { group: 'Statements', path: '/admin/statements/history', label: 'Statement History' },
  { group: 'Statements', path: '/admin/deductions-ledger', label: 'Deductions Ledger' },

  { group: 'Finance', path: '/admin/finance/year', label: 'Financial Year' },
  { group: 'Finance', path: '/admin/finance/income-vs-expenses', label: 'Income vs Expenses' },
  { group: 'Finance', path: '/admin/finance/income', label: 'Income' },
  { group: 'Finance', path: '/admin/finance/expenses', label: 'Expenses' },
  { group: 'Finance', path: '/admin/finance/vat', label: 'VAT Summary' },
  { group: 'Finance', path: '/admin/finance/forecast', label: 'Forecast' },
  { group: 'Finance', path: '/admin/finance/cashflow', label: 'Cash Flow' },
  { group: 'Finance', path: '/admin/finance/owner-driver-payouts', label: 'Owner Driver Payouts' },
  { group: 'Finance', path: '/admin/finance/transport-revenue', label: 'JSM Transport Revenue' },
  { group: 'Finance', path: '/admin/finance/transport-expenses', label: 'JSM Transport Expenses' },
  { group: 'Finance', path: '/admin/finance/transport-pnl', label: 'JSM Transport P&L' },
  { group: 'Finance', path: '/admin/finance/categories', label: 'Categories' },
  { group: 'Finance', path: '/admin/finance/loans', label: 'Loans' },
  { group: 'Finance', path: '/admin/fuel-tracker', label: 'Fuel Tracker' },
  { group: 'Finance', path: '/admin/procurement', label: 'Procurement' },

  { group: 'Banking', path: '/admin/banking/accounts', label: 'Bank Accounts' },
  { group: 'Banking', path: '/admin/banking/imports', label: 'Statement Imports' },
  { group: 'Banking', path: '/admin/banking/rules', label: 'Categorisation Rules' },
  { group: 'Banking', path: '/admin/banking/reconcile', label: 'Reconcile' },

  { group: 'Auto Spares', path: '/auto-spares/admin', label: 'Auto Spares Admin' },
  { group: 'Auto Spares', path: '/admin/menu-settings', label: 'Menu Visibility (this page)' },
];

const GROUPS = ['Top', 'Statements', 'Finance', 'Banking', 'Auto Spares'];

export default function MenuSettingsPage() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [hidden, setHidden] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/menu-visibility`, { headers });
        setHidden(new Set(r.data?.hidden || []));
      } catch (err) {
        toast.error('Failed to load menu settings');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePath = (path) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/menu-visibility`, { hidden: [...hidden] }, { headers });
      toast.success('Saved — sidebar updates on the next page load');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleShowAll = () => setHidden(new Set());

  const filtered = useMemo(() => {
    if (!query.trim()) return MENU_ITEMS;
    const q = query.trim().toLowerCase();
    return MENU_ITEMS.filter(m => m.label.toLowerCase().includes(q) || m.path.toLowerCase().includes(q));
  }, [query]);

  const grouped = useMemo(() => {
    const out = {};
    for (const g of GROUPS) out[g] = filtered.filter(m => m.group === g);
    return out;
  }, [filtered]);

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <EyeOff className="h-6 w-6 text-slate-600" /> Sidebar Menu Visibility
            </h1>
            <p className="text-sm text-muted-foreground">
              Hide modules you don't use. Toggle <b>Hidden</b> on to remove an item from everyone's sidebar.
              Routes still work directly via URL — only the menu link is hidden.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-3 text-muted-foreground" />
              <Input
                placeholder="Filter…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-8 w-56"
                data-testid="menu-settings-search"
              />
            </div>
            <Button variant="outline" onClick={handleShowAll} data-testid="show-all-btn">Show All</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-menu-btn">
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <div className="text-xs text-muted-foreground">
          {hidden.size === 0 ? (
            <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> All modules visible</span>
          ) : (
            <span className="inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> <b>{hidden.size}</b> module{hidden.size === 1 ? '' : 's'} hidden</span>
          )}
        </div>

        {loading ? (
          <div className="text-center py-10 text-muted-foreground">Loading…</div>
        ) : (
          GROUPS.map((g) => grouped[g]?.length > 0 && (
            <Card key={g}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm uppercase tracking-wide text-slate-600">{g}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {grouped[g].map((m) => {
                  const isHidden = hidden.has(m.path);
                  return (
                    <div key={m.path} className="flex items-center justify-between gap-3 py-2 px-2 hover:bg-slate-50 rounded">
                      <div className="min-w-0">
                        <div className={`font-medium ${isHidden ? 'line-through text-muted-foreground' : ''}`}>{m.label}</div>
                        <div className="text-[10px] font-mono text-muted-foreground truncate">{m.path}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-[11px] font-semibold ${isHidden ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {isHidden ? 'HIDDEN' : 'VISIBLE'}
                        </span>
                        <Switch
                          checked={isHidden}
                          onCheckedChange={() => togglePath(m.path)}
                          data-testid={`toggle-${m.path}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))
        )}

        <p className="text-xs text-muted-foreground">
          Tip: A hidden module is removed from the sidebar but the page still works if you type the URL directly. Useful for clean-up — delete the underlying data via the module itself.
        </p>
      </div>
    </DashboardLayout>
  );
}
